class CollectionTabs extends HTMLElement {
  constructor() {
    super();

    this.pills = [];
    this.hero = null;
    this.defaultBanner = null;
    this.activeHandle = '';
    this.allHandle = 'all';
    this.preloadedImages = new Set();
    this.desktopQuery = window.matchMedia('(min-width: 992px)');
  }

  connectedCallback() {
    this.list = this.querySelector('[data-collection-tabs-list]');
    this.allLabel = this.getAttribute('all-label') || 'All products';
    this.allHandle = this.handleize(this.allLabel) || 'all';
    this.hero = document.querySelector('[data-collection-hero]');

    this.storeDefaultBanner();
    this.buildPills();
    this.bindEvents();
    this.updateStickyOffset();
    this.applyTab(this.initialHandle(), { push: false, replace: true });
    this.schedulePreload();
  }

  disconnectedCallback() {
    this.unbindEvents();
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
    }
  }

  bindEvents() {
    this.onResize = () => {
      if (this.resizeFrame) return;
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateStickyOffset();
      });
    };
    this.onPopState = () => this.applyTab(this.initialHandle(), { push: false });
    this.onSectionLoad = () => {
      this.hero = document.querySelector('[data-collection-hero]');
      this.storeDefaultBanner();
      this.buildPills();
      this.updateStickyOffset();
      this.applyTab(this.activeHandle || this.initialHandle(), { push: false });
      this.schedulePreload();
    };
    this.onBreakpointChange = () => this.preloadBanners();
    this.onSectionSelect = (event) => {
      const group = event.target?.querySelector?.('[data-collection-group]');
      if (group?.dataset.tabHandle) {
        this.applyTab(group.dataset.tabHandle, { push: false });
      }
    };
    this.onKeyDown = (event) => this.handleKeydown(event);

    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onResize, { passive: true });
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:reorder', this.onSectionLoad);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    this.list?.addEventListener('keydown', this.onKeyDown);
    this.desktopQuery.addEventListener('change', this.onBreakpointChange);
  }

  unbindEvents() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onResize);
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('shopify:section:reorder', this.onSectionLoad);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    this.list?.removeEventListener('keydown', this.onKeyDown);
    this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
  }

  get groups() {
    return [...document.querySelectorAll('[data-collection-group]')];
  }

  initialHandle() {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (!tab) return this.allHandle;

    const match = this.groups.find((group) => group.dataset.tabHandle === tab);
    if (match || tab === this.allHandle) return tab;

    return this.allHandle;
  }

  storeDefaultBanner() {
    if (!this.hero) return;

    const payload = this.hero.querySelector('[data-collection-hero-default]');
    if (payload) {
      try {
        this.defaultBanner = JSON.parse(payload.textContent);
        return;
      } catch (error) {
        console.error(error);
      }
    }

    this.defaultBanner = this.readHeroState();
  }

  readHeroState() {
    if (!this.hero) return null;

    const heading = this.hero.querySelector('[data-hero-heading]');
    const subheading = this.hero.querySelector('[data-hero-subheading]');
    const desktopSource = this.hero.querySelector('[data-hero-desktop-source]');
    const image = this.hero.querySelector('[data-hero-image]');
    const badges = [...this.hero.querySelectorAll('[data-hero-badges] .ctr-collection-hero__badge')];

    return {
      heading: heading ? heading.innerHTML : '',
      subheading: subheading ? subheading.innerHTML : '',
      bgColor: this.hero.style.getPropertyValue('--ctr-collection-hero-bg') || '',
      headingColor: this.hero.style.getPropertyValue('--ctr-collection-hero-heading-color') || '',
      subheadingColor: this.hero.style.getPropertyValue('--ctr-collection-hero-subheading-color') || '',
      desktopImage: desktopSource ? desktopSource.getAttribute('srcset') : '',
      mobileImage: image ? image.getAttribute('src') : '',
      mobileSrcset: image ? image.getAttribute('srcset') : '',
      imageAlt: image ? image.getAttribute('alt') : '',
      layout: null,
      badges: badges.map((badge) => ({
        text: badge.innerHTML,
        textColor: badge.style.getPropertyValue('--ctr-badge-text'),
        backgroundColor: badge.style.getPropertyValue('--ctr-badge-bg'),
        borderColor: badge.style.getPropertyValue('--ctr-badge-border'),
      })),
    };
  }

  buildPills() {
    if (!this.list) return;

    this.list.innerHTML = '';

    this.list.appendChild(this.createPill(this.allHandle, this.allLabel, true));

    for (const group of this.groups) {
      const handle = group.dataset.tabHandle;
      const label = group.dataset.tabLabel || handle;
      if (!handle) continue;
      this.list.appendChild(this.createPill(handle, label, false));
    }

    this.pills = [...this.list.querySelectorAll('[data-collection-tab]')];
  }

  createPill(handle, label, isAll) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ctr-collection-tabs__tab';
    button.setAttribute('role', 'tab');
    button.setAttribute('data-collection-tab', handle);
    button.setAttribute('aria-selected', 'false');
    button.tabIndex = -1;
    button.innerHTML = label;
    if (isAll) button.setAttribute('data-tab-all', 'true');

    button.addEventListener('click', () => {
      this.applyTab(handle, { push: true, scroll: true });
    });

    return button;
  }

  applyTab(handle, { push = false, replace = false, scroll = false } = {}) {
    const nextHandle = handle || this.allHandle;
    this.activeHandle = nextHandle;
    const showAll = nextHandle === this.allHandle;

    for (const group of this.groups) {
      const isActive = showAll || group.dataset.tabHandle === nextHandle;
      group.hidden = !isActive;
      group.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      const wrapper = group.closest('.shopify-section');
      if (wrapper) wrapper.hidden = !isActive;
    }

    for (const pill of this.pills) {
      const isActive = pill.getAttribute('data-collection-tab') === nextHandle;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      pill.tabIndex = isActive ? 0 : -1;
    }

    this.applyBanner(showAll ? this.defaultBanner : this.readGroupBanner(nextHandle));
    this.updateUrl(nextHandle, { push, replace });

    if (scroll) this.scrollToTabs();
  }

  scrollToTabs() {
    const wrapper = this.closest('.shopify-section') || this;
    const stickyOffset = parseFloat(
      getComputedStyle(wrapper).getPropertyValue('--ctr-collection-tabs-top'),
    );
    const offset = Number.isNaN(stickyOffset) ? 0 : stickyOffset;
    const anchor = this.scrollAnchor();
    if (!anchor) return;

    const tabsHeight = anchor === wrapper ? 0 : wrapper.getBoundingClientRect().height;
    const top = anchor.getBoundingClientRect().top + window.scrollY - offset - tabsHeight;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  scrollAnchor() {
    const group = this.groups.find((item) => !item.hidden);
    const section = group?.closest('.shopify-section');

    return section || group || this.closest('.shopify-section') || this;
  }

  readGroupBanner(handle) {
    const group = this.groups.find((item) => item.dataset.tabHandle === handle);
    return this.parseGroupBanner(group) || this.defaultBanner;
  }

  parseGroupBanner(group) {
    const payload = group?.querySelector('[data-collection-banner]');
    if (!payload) return null;

    try {
      return JSON.parse(payload.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  schedulePreload() {
    const run = () => this.preloadBanners();

    if (document.readyState === 'complete') {
      this.whenIdle(run);
      return;
    }

    window.addEventListener('load', () => this.whenIdle(run), { once: true });
  }

  whenIdle(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 2000 });
      return;
    }

    setTimeout(callback, 500);
  }

  preloadBanners() {
    const banners = [this.defaultBanner];

    for (const group of this.groups) {
      const banner = this.parseGroupBanner(group);
      if (banner) banners.push(banner);
    }

    for (const banner of banners) {
      this.warmBanner(banner);
    }
  }

  warmBanner(banner) {
    if (!banner) return;

    if (this.desktopQuery.matches) {
      this.warmImage(banner.desktopImage || banner.mobileImage);
      return;
    }

    const mobileImage = banner.mobileImage || banner.desktopImage;
    const mobileSrcset = banner.mobileImage ? banner.mobileSrcset : '';
    this.warmImage(mobileImage, mobileSrcset);
  }

  warmImage(url, srcset) {
    const key = srcset || url;
    if (!url || this.preloadedImages.has(key)) return;

    this.preloadedImages.add(key);

    const image = new Image();
    image.decoding = 'async';
    if ('fetchPriority' in image) image.fetchPriority = 'low';
    if (srcset) {
      image.sizes = '100vw';
      image.srcset = srcset;
    }
    image.src = url;
  }

  applyBanner(banner) {
    if (!this.hero || !banner) return;

    const heading = this.hero.querySelector('[data-hero-heading]');
    const subheading = this.hero.querySelector('[data-hero-subheading]');
    const desktopSource = this.hero.querySelector('[data-hero-desktop-source]');
    const image = this.hero.querySelector('[data-hero-image]');
    const media = this.hero.querySelector('[data-hero-media]');

    if (heading && banner.heading != null) {
      heading.innerHTML = banner.heading;
      heading.hidden = banner.heading === '';
    }

    if (subheading && banner.subheading != null) {
      subheading.innerHTML = banner.subheading;
      subheading.hidden = banner.subheading === '';
    }

    if (banner.bgColor) {
      this.hero.style.setProperty('--ctr-collection-hero-bg', banner.bgColor);
    } else {
      this.hero.style.removeProperty('--ctr-collection-hero-bg');
    }

    if (banner.headingColor) {
      this.hero.style.setProperty('--ctr-collection-hero-heading-color', banner.headingColor);
    } else {
      this.hero.style.removeProperty('--ctr-collection-hero-heading-color');
    }

    if (banner.subheadingColor) {
      this.hero.style.setProperty('--ctr-collection-hero-subheading-color', banner.subheadingColor);
    } else {
      this.hero.style.removeProperty('--ctr-collection-hero-subheading-color');
    }

    this.applyLayout(banner.layout);
    this.applyBadges(banner.badges);

    const desktopImage = banner.desktopImage || banner.mobileImage || '';
    const mobileImage = banner.mobileImage || banner.desktopImage || '';
    const mobileSrcset = banner.mobileImage ? banner.mobileSrcset || '' : '';
    const alt = banner.imageAlt || banner.desktopImageAlt || banner.mobileImageAlt || '';

    this.warmBanner(banner);

    if (desktopSource) {
      desktopSource.setAttribute('srcset', desktopImage || '');
    }

    if (image) {
      if (mobileImage) {
        if (mobileSrcset) {
          image.setAttribute('srcset', mobileSrcset);
        } else {
          image.removeAttribute('srcset');
        }
        image.setAttribute('src', mobileImage);
        image.hidden = false;
      } else {
        image.removeAttribute('srcset');
        image.removeAttribute('src');
        image.hidden = true;
      }
      if (alt) image.setAttribute('alt', alt);
    }

    if (media) {
      media.hidden = !desktopImage && !mobileImage;
    }
  }

  applyBadges(badges) {
    const container = this.hero.querySelector('[data-hero-badges]');
    if (!container) return;

    container.innerHTML = '';

    for (const badge of badges || []) {
      if (!badge?.text) continue;

      const item = document.createElement('span');
      item.className = 'ctr-collection-hero__badge';
      if (badge.textColor) item.style.setProperty('--ctr-badge-text', badge.textColor);
      if (badge.backgroundColor) item.style.setProperty('--ctr-badge-bg', badge.backgroundColor);
      if (badge.borderColor) item.style.setProperty('--ctr-badge-border', badge.borderColor);
      item.innerHTML = badge.text;
      container.appendChild(item);
    }

    container.hidden = container.children.length === 0;
  }

  applyLayout(layout) {
    if (!this.hero) return;

    this.setLayoutVars(layout?.desktop, '-desktop');
    this.setLayoutVars(layout?.mobile, '');
  }

  setLayoutVars(values, suffix) {
    const align = { left: 'flex-start', center: 'center', right: 'flex-end' };
    const justify = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
    const textAlign = { left: 'start', center: 'center', right: 'end' };

    this.setHeroVar(`--ctr-collection-hero-min-height${suffix}`, this.toPx(values?.minHeight));
    this.setHeroVar(`--ctr-collection-hero-align${suffix}`, align[values?.horizontal]);
    this.setHeroVar(`--ctr-collection-hero-justify${suffix}`, justify[values?.vertical]);
    this.setHeroVar(`--ctr-collection-hero-text-align${suffix}`, textAlign[values?.textAlign]);
    this.setHeroVar(
      `--ctr-collection-hero-content-max-width${suffix}`,
      values?.maxWidth === 0 ? 'none' : this.toPx(values?.maxWidth),
    );
    this.setHeroVar(`--ctr-collection-hero-padding-top${suffix}`, this.toPx(values?.paddingTop));
    this.setHeroVar(
      `--ctr-collection-hero-padding-bottom${suffix}`,
      this.toPx(values?.paddingBottom),
    );
  }

  setHeroVar(name, value) {
    if (value == null || value === '') {
      this.hero.style.removeProperty(name);
      return;
    }

    this.hero.style.setProperty(name, value);
  }

  toPx(value) {
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    return `${value}px`;
  }

  updateUrl(handle, { push, replace }) {
    const url = new URL(window.location.href);

    if (handle === this.allHandle) {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', handle);
    }

    const next = `${url.pathname}${url.search}${url.hash}`;
    if (replace) {
      history.replaceState({ tab: handle }, '', next);
      return;
    }

    if (push) {
      history.pushState({ tab: handle }, '', next);
    }
  }

  handleKeydown(event) {
    if (!this.pills.length) return;

    const currentIndex = this.pills.findIndex((pill) => pill.getAttribute('aria-selected') === 'true');
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.pills.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + this.pills.length) % this.pills.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.pills.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.pills[nextIndex].focus();
    this.applyTab(this.pills[nextIndex].getAttribute('data-collection-tab'), {
      push: true,
      scroll: true,
    });
  }

  updateStickyOffset() {
    const header = document.querySelector('.ctr-header-section, .header, sticky-header');
    const offset = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    const wrapper = this.closest('.shopify-section') || this;
    wrapper.style.setProperty('--ctr-collection-tabs-top', `${offset}px`);
  }

  handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}
